<template>
  <v-dialog v-model="dialog" width="900" persistent scrollable>
    <v-card v-if="loading || !studentData" class="secondary" flat>
      <v-card-title class="font-weight-medium secondary--text primary py-3">
        <v-skeleton-loader
          type="text"
          width="200"
          class="mr-2"
        ></v-skeleton-loader>
        <v-spacer></v-spacer>
        <v-btn small icon disabled>
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-container fluid class="pa-4">
          <v-row class="mb-3">
            <v-col cols="12">
              <v-card outlined class="pa-4">
                <v-row align="center">
                  <v-col cols="12" md="3" class="text-center">
                    <v-skeleton-loader
                      type="avatar"
                      class="mx-auto"
                      width="120"
                      height="120"
                    ></v-skeleton-loader>
                  </v-col>
                  <v-col cols="12" md="9">
                    <v-row dense>
                      <v-col cols="12">
                        <v-skeleton-loader
                          type="text"
                          width="250"
                        ></v-skeleton-loader>
                        <v-skeleton-loader
                          type="text"
                          width="150"
                          class="mt-1"
                        ></v-skeleton-loader>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-skeleton-loader
                          type="text"
                          width="100"
                        ></v-skeleton-loader>
                        <v-skeleton-loader
                          type="text"
                          width="80"
                          class="mt-1"
                        ></v-skeleton-loader>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-skeleton-loader
                          type="text"
                          width="100"
                        ></v-skeleton-loader>
                        <v-skeleton-loader
                          type="text"
                          width="80"
                          class="mt-1"
                        ></v-skeleton-loader>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-skeleton-loader
                          type="text"
                          width="100"
                        ></v-skeleton-loader>
                        <v-skeleton-loader
                          type="text"
                          width="80"
                          class="mt-1"
                        ></v-skeleton-loader>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-3" v-for="i in 3" :key="i">
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle class="pb-2">
                  <v-skeleton-loader
                    type="text"
                    width="150"
                  ></v-skeleton-loader>
                </v-card-subtitle>
                <v-divider></v-divider>
                <v-card-text class="pt-3">
                  <v-row dense>
                    <v-col cols="12" sm="6" md="4" v-for="j in 3" :key="j">
                      <v-skeleton-loader
                        type="text"
                        width="100"
                      ></v-skeleton-loader>
                      <v-skeleton-loader
                        type="text"
                        width="80"
                        class="mt-1"
                      ></v-skeleton-loader>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-3">
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle class="pb-2">
                  <v-skeleton-loader
                    type="text"
                    width="150"
                  ></v-skeleton-loader>
                </v-card-subtitle>
                <v-divider></v-divider>
                <v-card-text class="pt-3">
                  <v-row dense>
                    <v-col cols="12" v-for="j in 3" :key="j">
                      <v-skeleton-loader
                        type="text"
                        width="100%"
                        height="40"
                      ></v-skeleton-loader>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <v-card v-else class="secondary" flat>
      <v-card-title
        class="font-weight-medium secondary--text primary py-3 animate__animated animate__fadeInDown"
      >
        <v-icon class="mr-2" color="secondary">mdi-account-circle</v-icon>
        <span class="text-truncate">
          {{ studentData.student_info?.name || "..." }} -
          {{ studentData.usn_id }}
        </span>
        <v-spacer></v-spacer>
        <v-btn
          small
          icon
          @click="$emit('close-dialog')"
          class="animate__animated animate__pulse animate__infinite animate__slower"
        >
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-container fluid class="pa-4">
          <v-row class="mb-3 animate__animated animate__fadeIn animate__faster">
            <v-col cols="12">
              <v-card outlined class="pa-4 elevation-1 student-profile-card">
                <v-row align="center">
                  <v-col cols="12" md="3" class="text-center">
                    <v-hover v-slot="{ hover }">
                      <div class="profile-image-container">
                        <v-avatar
                          size="120"
                          color="blue-grey lighten-5"
                          class="transition-elevation"
                          :class="{ 'elevation-4': hover }"
                        >
                          <transition name="fade" mode="out-in">
                            <v-img
                              v-if="profilePreview"
                              :src="profilePreview"
                              alt="Profile"
                              class="profile-image"
                              @error="handleImageError"
                            >
                              <template v-slot:placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="primary"
                                  ></v-progress-circular>
                                </v-row>
                              </template>
                            </v-img>
                            <div
                              v-else-if="imageLoading"
                              class="d-flex fill-height align-center justify-center"
                            >
                              <v-progress-circular
                                indeterminate
                                color="primary"
                                size="24"
                              ></v-progress-circular>
                            </div>
                            <v-icon
                              v-else
                              size="64"
                              color="blue-grey lighten-2"
                              class="animate__animated animate__pulse animate__infinite animate__slower"
                            >
                              mdi-account
                            </v-icon>
                          </transition>
                        </v-avatar>
                      </div>
                    </v-hover>
                  </v-col>

                  <v-col cols="12" md="9">
                    <v-row dense>
                      <v-col cols="12">
                        <div class="d-flex align-start justify-space-between">
                          <div>
                            <div
                              class="text-h5 font-weight-bold primary--text mb-1"
                            >
                              {{ studentData.student_info?.name || "..." }}
                            </div>
                            <div
                              class="text-body-2 blue-grey--text"
                              v-if="studentData.current_term_no"
                            >
                              Current Term {{ studentData.current_term_no }}
                            </div>
                          </div>
                          <div class="d-flex align-center flex-wrap" style="gap: 8px">
                            <v-chip
                              v-if="studentData.partner_id"
                              small
                              color="orange lighten-5"
                              text-color="orange darken-3"
                              class="font-weight-medium"
                            >
                              <v-icon x-small left>mdi-account-group</v-icon>
                              {{ getPartnerName(studentData.partner_id) }}
                            </v-chip>
                            <v-chip
                              v-if="studentData.current_status"
                              small
                              :color="getStatusColor(studentData.current_status)"
                              text-color="white"
                              class="font-weight-medium"
                            >
                              {{ studentData.current_status }}
                            </v-chip>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12">
                        <v-divider class="my-2"></v-divider>
                      </v-col>

                      <v-col cols="12" sm="6" md="4">
                        <div class="info-item">
                          <div class="info-label">USN</div>
                          <div class="info-value">{{ studentData.usn_id }}</div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <div class="info-item">
                          <div class="info-label">Batch</div>
                          <div class="info-value">
                            {{ studentData.admitted_year }} -
                            {{ studentData.admitted_batch }}
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <div
                          class="info-item"
                          v-if="studentData.application_id"
                        >
                          <div class="info-label">Application ID</div>
                          <div class="info-value">
                            {{ studentData.application_id }}
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row
            class="mb-3 animate__animated animate__fadeInUp animate__delay-4s"
          >
            <v-col cols="12">
              <v-card outlined class="hover-card payments-card">
                <v-card-subtitle class="section-title d-flex align-center">
                  <v-icon small class="mr-2" color="green"
                    >mdi-attachment</v-icon
                  >
                  <span class="font-weight-bold">Attachments</span>
                </v-card-subtitle>
                <v-divider class="mb-2"></v-divider>
                <v-card-text class="pt-3 pb-1">
                  <div v-if="loadingAttachments" class="text-center py-8">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                    <div class="text-body-2 grey--text mt-2">
                      Loading attachments...
                    </div>
                  </div>

                  <div
                    v-else-if="sortedApplicantAttachments.length === 0"
                    class="text-center py-3"
                  >
                    <v-icon size="24" color="grey lighten-2"
                      >mdi-file-document-outline</v-icon
                    >
                    <div class="text-body-2 grey--text font-weight-medium">
                      No Attachments
                    </div>
                    <div class="text-caption grey--text">
                      No documents found for this student.
                    </div>
                  </div>

                  <div v-else>
                    <v-row>
                      <v-col
                        v-for="(attachment, index) in sortedApplicantAttachments"
                        :key="index"
                        cols="12"
                        sm="6"
                        md="4"
                      >
                        <v-card outlined class="attachment-card" hover>
                          <v-card-text class="pa-3">
                            <div
                              class="d-flex justify-space-between align-center mb-2"
                            >
                              <v-chip
                                x-small
                                :color="getDocTypeColor(attachment.name)"
                                text-color="white"
                              >
                                {{ getDocType(attachment.name) }}
                              </v-chip>
                              <v-btn
                                x-small
                                color="success"
                                outlined
                                @click="downloadAttachment(attachment)"
                              >
                                <v-icon x-small>mdi-download</v-icon>
                              </v-btn>
                            </div>

                            <div class="attachment-preview mb-2">
                              <v-img
                                v-if="isImage(attachment.type)"
                                :src="attachment.blobUrl"
                                aspect-ratio="1.5"
                                class="rounded cursor-pointer"
                                contain
                                style="height: 80px !important"
                                @click="viewAttachment(attachment)"
                              >
                                <template v-slot:placeholder>
                                  <v-row
                                    class="fill-height ma-0"
                                    align="center"
                                    justify="center"
                                  >
                                    <v-progress-circular
                                      indeterminate
                                      color="grey lighten-2"
                                    ></v-progress-circular>
                                  </v-row>
                                </template>
                              </v-img>

                              <div
                                v-else-if="isPDF(attachment.type)"
                                class="pdf-preview rounded cursor-pointer"
                                @click="viewAttachment(attachment)"
                              >
                                <v-icon size="50" color="red darken-1"
                                  >mdi-file-pdf-box</v-icon
                                >
                                <div class="text-caption mt-2">
                                  Click to view PDF
                                </div>
                              </div>

                              <div
                                v-else
                                class="other-file-preview rounded cursor-pointer"
                                @click="viewAttachment(attachment)"
                              >
                                <v-icon size="80" color="grey"
                                  >mdi-file-document</v-icon
                                >
                                <div class="text-caption mt-2">
                                  {{ attachment.type }}
                                </div>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row
            v-if="sortedProofs.length"
            class="mb-3 animate__animated animate__fadeInUp animate__delay-4s"
          >
            <v-col cols="12">
              <v-card outlined class="hover-card payments-card">
                <v-card-subtitle class="section-title d-flex align-center">
                  <v-icon small class="mr-2" color="deep-purple"
                    >mdi-file-certificate-outline</v-icon
                  >
                  <span class="font-weight-bold">Proofs</span>
                  <v-spacer></v-spacer>
                  <span class="text-caption grey--text"
                    >{{ sortedProofs.length }} document{{
                      sortedProofs.length !== 1 ? "s" : ""
                    }}</span
                  >
                </v-card-subtitle>
                <v-divider class="mb-2"></v-divider>
                <v-card-text class="pt-3 pb-1">
                  <v-row>
                    <v-col
                      v-for="(proof, index) in sortedProofs"
                      :key="'proof-' + index"
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-card outlined class="attachment-card" hover>
                        <v-card-text class="pa-3">
                          <div
                            class="d-flex justify-space-between align-center mb-2"
                          >
                            <v-chip
                              x-small
                              :color="getProofColor(proof.name)"
                              text-color="white"
                            >
                              {{ getProofLabel(proof.name) }}
                            </v-chip>
                            <v-btn
                              x-small
                              color="success"
                              outlined
                              @click="downloadAttachment(proof)"
                            >
                              <v-icon x-small>mdi-download</v-icon>
                            </v-btn>
                          </div>

                          <div class="attachment-preview mb-2">
                            <v-img
                              v-if="isImage(proof.type)"
                              :src="proof.blobUrl"
                              aspect-ratio="1.5"
                              class="rounded cursor-pointer"
                              contain
                              style="height: 80px !important"
                              @click="viewAttachment(proof)"
                            >
                              <template v-slot:placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="grey lighten-2"
                                  ></v-progress-circular>
                                </v-row>
                              </template>
                            </v-img>

                            <div
                              v-else-if="isPDF(proof.type)"
                              class="pdf-preview rounded cursor-pointer"
                              @click="viewAttachment(proof)"
                            >
                              <v-icon size="50" color="red darken-1"
                                >mdi-file-pdf-box</v-icon
                              >
                              <div class="text-caption mt-2">
                                Click to view PDF
                              </div>
                            </div>

                            <div
                              v-else
                              class="other-file-preview rounded cursor-pointer"
                              @click="viewAttachment(proof)"
                            >
                              <v-icon size="80" color="grey"
                                >mdi-file-document</v-icon
                              >
                              <div class="text-caption mt-2">
                                {{ proof.type }}
                              </div>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <div
            v-for="(section, index) in informationSections"
            :key="section.title"
          >
            <v-row
              class="mb-3"
              :class="`animate__animated animate__fadeInUp animate__delay-${
                index + 1
              }s`"
            >
              <v-col cols="12">
                <v-card outlined class="elevation-1 hover-card section-card">
                  <v-card-subtitle class="section-title d-flex align-center">
                    <v-icon small class="mr-2" :color="section.color">{{
                      section.icon
                    }}</v-icon>
                    <span class="font-weight-bold">{{ section.title }}</span>
                  </v-card-subtitle>
                  <v-divider class="mb-2"></v-divider>
                  <v-card-text class="pt-3">
                    <v-row dense>
                      <v-col
                        v-for="field in section.fields"
                        :key="field.key"
                        cols="12"
                        :sm="field.size || 6"
                        :md="field.size || 4"
                      >
                        <div
                          class="field-item"
                          :class="{ 'has-data': hasData(field.key) }"
                        >
                          <div class="field-label">{{ field.label }}</div>
                          <div class="field-value">
                            <v-chip
                              v-if="field.chip && hasData(field.key)"
                              small
                              :color="field.chipColor || 'blue-grey lighten-5'"
                              class="font-weight-medium"
                            >
                              {{ getFieldValue(field.key) }}
                            </v-chip>
                            <span v-else>{{ getFieldValue(field.key) }}</span>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
          <v-row
            class="mb-3 animate__animated animate__fadeInUp animate__delay-3s"
          >
            <v-col cols="12">
              <v-card
                outlined
                class="hover-card"
                @click="openScorecard()"
                style="cursor: pointer"
              >
                <v-card-text class="d-flex align-center py-4">
                  <v-icon color="primary" class="mr-3" size="28"
                    >mdi-certificate-outline</v-icon
                  >
                  <div>
                    <div class="font-weight-bold">Consolidated Scorecard</div>
                    <div class="text-caption grey--text">
                      View subject-wise marks, grades &amp; result across all
                      semesters
                    </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click.stop="openScorecard()">
                    View <v-icon right small>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row
            class="mb-3 animate__animated animate__fadeInUp animate__delay-4s"
          >
            <v-col cols="12">
              <v-card outlined class="hover-card payments-card">
                <v-card-subtitle class="section-title d-flex align-center">
                  <v-icon small class="mr-2" color="green"
                    >mdi-credit-card-multiple</v-icon
                  >
                  <span class="font-weight-bold">Payment History</span>
                  <v-spacer></v-spacer>
                  <div class="d-flex align-center">
                    <span class="text-caption mr-2"
                      >{{ payments.length }} receipt{{
                        payments.length !== 1 ? "s" : ""
                      }}</span
                    >
                    <v-chip
                      v-if="totalAmount"
                      small
                      color="success lighten-5"
                      text-color="success darken-2"
                      >{{ totalAmount }}</v-chip
                    >
                  </div>
                </v-card-subtitle>
                <v-divider class="mb-2"></v-divider>
                <v-card-text class="pt-3 pb-1">
                  <div v-if="payments.length === 0" class="text-center py-8">
                    <v-icon size="40" color="grey lighten-2" class="mb-4"
                      >mdi-credit-card-off</v-icon
                    >
                    <div class="text-h6 grey--text mb-2">
                      No Payment Records
                    </div>
                    <div class="text-body-2 grey--text">
                      No payment history found for this student.
                    </div>
                  </div>

                  <div v-else class="payment-table-wrapper">
                    <v-simple-table class="payment-table">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left" style="width: 100px">Date</th>
                            <th class="text-left" style="width: 150px">
                              Reference No.
                            </th>
                            <th class="text-left">Payment Term</th>
                            <th class="text-left">Particular</th>
                            <th class="text-left" style="width: 120px">
                              Amount
                            </th>
                            <th class="text-left" style="width: 100px">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(payment, index) in displayedPayments"
                            :key="payment.reference_number + index"
                            class="payment-row"
                            :class="{
                              'last-row':
                                index === displayedPayments.length - 1,
                            }"
                          >
                            <td>
                              <div class="payment-date">{{ payment.date }}</div>
                            </td>
                            <td>
                              <v-chip
                                small
                                color="blue-grey lighten-5"
                                class="font-weight-medium reference-chip"
                              >
                                {{ payment.reference_number }}
                              </v-chip>
                            </td>
                            <td>
                              <div class="payment-particular">
                                {{ payment.payment_term }}
                              </div>
                            </td>
                            <td>
                              <div class="payment-particular">
                                {{ payment.particular }}
                              </div>
                            </td>
                            <td
                              class="font-weight-bold success--text payment-amount"
                            >
                              {{ "₹" + formatAmount(payment.amount) }}
                            </td>
                            <td>
                              <div class="d-flex">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      x-small
                                      color="primary"
                                      class="action-btn"
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="downloadReceipt(payment)"
                                    >
                                      <v-icon x-small>mdi-download</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Download Receipt</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      x-small
                                      color="info"
                                      class="action-btn ml-1"
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="viewPaymentDetails(payment)"
                                    >
                                      <v-icon x-small>mdi-eye</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>View Details</span>
                                </v-tooltip>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>

                    <div
                      v-if="payments.length > maxVisiblePayments"
                      class="text-center mt-3"
                    >
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="showAllPayments = !showAllPayments"
                        class="view-more-btn"
                      >
                        <v-icon small left>{{
                          showAllPayments
                            ? "mdi-chevron-up"
                            : "mdi-chevron-down"
                        }}</v-icon>
                        {{
                          showAllPayments
                            ? "Show Less"
                            : `View All ${payments.length} Payments`
                        }}
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <v-dialog v-model="paymentDetailsDialog" max-width="500">
      <v-card v-if="selectedPayment">
        <v-card-title class="primary white--text">
          Payment Details
          <v-spacer></v-spacer>
          <v-btn icon dark @click="paymentDetailsDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="pa-4">
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Date</v-list-item-title>
                <v-list-item-subtitle>{{
                  selectedPayment.date
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Reference Number</v-list-item-title>
                <v-list-item-subtitle class="font-weight-medium">{{
                  selectedPayment.reference_number
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Particular</v-list-item-title>
                <v-list-item-subtitle>{{
                  selectedPayment.particular
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Amount</v-list-item-title>
                <v-list-item-subtitle class="font-weight-bold success--text">{{
                  selectedPayment.amount
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="downloadReceipt(selectedPayment)">
            <v-icon left small>mdi-download</v-icon>Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="92%" width="960px" persistent>
      <div v-if="selectedAttachment" class="av-root">
        <div class="av-header d-flex align-center px-3">
          <v-btn
            v-if="sortedAttachments.length > 1"
            icon
            small
            :disabled="currentAttachmentIndex === 0"
            @click="showPreviousAttachment"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div class="av-title mx-2">
            <span class="text-subtitle-2 font-weight-medium">{{
              selectedAttachment.name
            }}</span>
            <span
              v-if="sortedAttachments.length > 1"
              class="text-caption grey--text ml-2"
            >
              {{ currentAttachmentIndex + 1 }} / {{ sortedAttachments.length }}
            </span>
          </div>

          <v-btn
            v-if="sortedAttachments.length > 1"
            icon
            small
            :disabled="currentAttachmentIndex === sortedAttachments.length - 1"
            @click="showNextAttachment"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <v-spacer></v-spacer>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="mr-1"
                v-bind="attrs"
                v-on="on"
                @click="downloadAttachment(selectedAttachment)"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Download</span>
          </v-tooltip>

          <v-btn icon small @click="closeViewer">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="av-divider"></div>

        <div
          v-if="isImage(selectedAttachment.type)"
          class="av-image-stage"
          ref="imageStage"
          @wheel.prevent="handleWheel"
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
          @touchstart.prevent="handleTouchStart"
          @touchmove.prevent="handleTouchMove"
          @touchend="handleTouchEnd"
          :style="{
            cursor: isPanning ? 'grabbing' : zoomLevel > 1 ? 'grab' : 'default',
          }"
        >
          <div
            class="av-image-canvas"
            :style="{
              transform: `translate(${panX}px, ${panY}px) scale(${zoomLevel}) rotate(${rotateDeg}deg)`,
              transformOrigin: 'center center',
              transition: isDragging ? 'none' : 'transform 0.18s ease',
            }"
          >
            <img
              :src="selectedAttachment.blobUrl"
              class="av-img"
              draggable="false"
              @load="onImageLoad"
            />
          </div>

          <div class="av-zoom-bar">
            <button
              class="av-zoom-btn"
              @click="zoomOut"
              :disabled="zoomLevel <= minZoom"
            >
              <v-icon small color="white">mdi-minus</v-icon>
            </button>
            <span class="av-zoom-pct">{{ Math.round(zoomLevel * 100) }}%</span>
            <button
              class="av-zoom-btn"
              @click="zoomIn"
              :disabled="zoomLevel >= maxZoom"
            >
              <v-icon small color="white">mdi-plus</v-icon>
            </button>
            <span class="av-zoom-sep"></span>
            <button class="av-zoom-btn" @click="rotateLeft" title="Rotate Left">
              <v-icon small color="white">mdi-rotate-left</v-icon>
            </button>
            <button
              class="av-zoom-btn"
              @click="rotateRight"
              title="Rotate Right"
            >
              <v-icon small color="white">mdi-rotate-right</v-icon>
            </button>
            <button
              class="av-zoom-btn"
              @click="resetRotate"
              title="Reset Rotation"
            >
              <v-icon small color="white">mdi-rotate-orbit</v-icon>
            </button>

            <span class="av-zoom-sep"></span>
            <button
              class="av-zoom-btn"
              @click="resetView"
              title="Reset view (0)"
            >
              <v-icon small color="white">mdi-fit-to-screen</v-icon>
            </button>
          </div>
        </div>

        <div v-else-if="isPDF(selectedAttachment.type)" class="av-pdf-stage">
          <iframe
            :src="
              selectedAttachment.blobUrl + '#toolbar=1&navpanes=0&scrollbar=1'
            "
            width="100%"
            height="100%"
            frameborder="0"
            style="display: block; border: none"
          ></iframe>
        </div>

        <div v-else class="av-unsupported">
          <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
          <p class="av-unsupported-title">Preview not available</p>
          <p class="av-unsupported-sub">
            Download the file to view its contents
          </p>
          <v-btn
            color="primary"
            outlined
            small
            @click="downloadAttachment(selectedAttachment)"
          >
            <v-icon left small>mdi-download</v-icon>Download File
          </v-btn>
        </div>

        <template v-if="sortedAttachments.length > 1">
          <div class="av-divider"></div>
          <div class="av-thumb-strip" ref="thumbStrip">
            <div
              v-for="(att, idx) in sortedAttachments"
              :key="idx"
              class="av-thumb-item"
              :class="{ 'av-thumb-active': currentAttachmentIndex === idx }"
              @click="goToAttachment(idx)"
              :ref="`thumb_${idx}`"
            >
              <div class="av-thumb-box">
                <img
                  v-if="isImage(att.type) && att.blobUrl"
                  :src="att.blobUrl"
                  class="av-thumb-img"
                  draggable="false"
                />
                <div v-else-if="isPDF(att.type)" class="av-thumb-icon">
                  <v-icon color="red darken-1" size="26"
                    >mdi-file-pdf-box</v-icon
                  >
                </div>
                <div v-else class="av-thumb-icon">
                  <v-icon color="grey" size="26">mdi-file-document</v-icon>
                </div>
              </div>
              <div class="av-thumb-label">{{ getShortFileName(att.name) }}</div>
            </div>
          </div>
        </template>
      </div>
    </v-dialog>
    <ConsolidatedScorecardDialog
      v-if="scorecardDialog"
      :dialog="scorecardDialog"
      :usn-id="studentData.usn_id"
      @close-dialog="closeScorecard"
    />
  </v-dialog>
</template>

<script>
import ConsolidatedScorecardDialog from "@/components/ConsolidatedScorecardDialog.vue";
export default {
  name: "StudentDialog",
  components: {
    ConsolidatedScorecardDialog,
  },
  props: {
    dialog: { type: Boolean, required: true },
    studentData: { type: Object, required: true, default: () => ({}) },
  },
  emits: ["close-dialog"],

  data() {
    return {
      loading: false,
      profilePreview: null,
      imageLoading: false,
      imageError: false,
      payments: [],
      showAllPayments: false,
      maxVisiblePayments: 5,
      paymentDetailsDialog: false,
      selectedPayment: null,
      partners: [],

      attachments: [],
      loadingAttachments: false,

      proofs: [],
      loadingProofs: false,

      viewDialog: false,
      selectedAttachment: null,
      currentAttachmentIndex: 0,

      zoomLevel: 1,
      minZoom: 0.25,
      maxZoom: 5,
      panX: 0,
      panY: 0,
      isPanning: false,
      isDragging: false,
      rotateDeg: 0,
      drag: {
        active: false,
        startX: 0,
        startY: 0,
        originPanX: 0,
        originPanY: 0,
      },

      _touch: {
        lastDist: null,
        startX: null,
        startY: null,
      },

      informationSections: [
        {
          title: "Personal Information",
          icon: "mdi-account",
          color: "purple",
          fields: [
            { key: "student_info.deb_id", label: "DEB ID", size: 4 },
            { key: "student_info.abc_id", label: "ABC ID", size: 4 },
            { key: "student_info.dob", label: "Date of Birth", size: 4 },
            { key: "student_info.gender", label: "Gender", size: 4 },
            {
              key: "student_info.aadhar_card_no",
              label: "Aadhar Number",
              size: 4,
            },
            { key: "student_info.pan_card_no", label: "PAN Number", size: 4 },
          ],
        },
        {
          title: "Contact Information",
          icon: "mdi-phone",
          color: "teal",
          fields: [
            { key: "student_info.email", label: "Email", size: 6 },
            {
              key: "student_info.alternate_email",
              label: "Alternate Email",
              size: 6,
            },
            { key: "student_info.mobile_no", label: "Mobile Number", size: 6 },
            {
              key: "student_info.whatsapp_mobile_no",
              label: "WhatsApp Number",
              size: 6,
            },
          ],
        },
        {
          title: "Address Information",
          icon: "mdi-map-marker",
          color: "orange",
          fields: [
            {
              key: "student_info.currentaddress",
              label: "Communication Address",
              show: false,
              size: 12,
            },
            {
              key: "student_info.current_address.address_line1",
              label: "Address Line 1",
              size: 6,
            },
            {
              key: "student_info.current_address.address_line2",
              label: "Address Line 2",
              size: 6,
            },
            {
              key: "student_info.current_address.city",
              label: "City",
              size: 3,
            },
            {
              key: "student_info.current_address.state",
              label: "State",
              size: 3,
            },
            {
              key: "student_info.current_address.country",
              label: "Country",
              size: 3,
            },
            {
              key: "student_info.current_address.postcode",
              label: "Postcode",
              size: 3,
            },
            {
              key: "student_info.permanentaddress",
              label: "Permanent Address",
              icon: "mdi-home",
              show: false,
              size: 12,
            },
            {
              key: "student_info.permanent_address.address_line1",
              label: "Address Line 1",
              size: 6,
            },
            {
              key: "student_info.permanent_address.address_line2",
              label: "Address Line 2",
              size: 6,
            },
            {
              key: "student_info.permanent_address.city",
              label: "City",
              size: 3,
            },
            {
              key: "student_info.permanent_address.state",
              label: "State",
              size: 3,
            },
            {
              key: "student_info.permanent_address.country",
              label: "Country",
              size: 3,
            },
            {
              key: "student_info.permanent_address.postcode",
              label: "Postcode",
              size: 3,
            },
          ],
        },
      ],
      scorecardDialog: false,
    };
  },

  created() {
    this.gotoTop();
    this.getPartners();
  },

  mounted() {
    window.addEventListener("keydown", this.handleKeyNavigation);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyNavigation);
    this.cleanup();
  },

  computed: {
    displayedPayments() {
      return this.showAllPayments
        ? this.payments
        : this.payments.slice(0, this.maxVisiblePayments);
    },

    totalAmount() {
      if (this.payments.length === 0) return null;
      const total = this.payments.reduce((sum, payment) => {
        const amount =
          parseFloat(payment.amount.replace(/[^0-9.-]+/g, "")) || 0;
        return sum + amount;
      }, 0);
      return `₹${total.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    sortedApplicantAttachments() {
      return this.attachments
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    sortedProofs() {
      return this.proofs.slice().sort((a, b) => a.name.localeCompare(b.name));
    },

    sortedAttachments() {
      return [...this.sortedApplicantAttachments, ...this.sortedProofs];
    },

    isPermanentAddressSame() {
      return (
        this.studentData?.student_info?.is_permanent_address_same === "true" ||
        this.studentData?.student_info?.is_permanent_address_same === true
      );
    },
  },

  watch: {
    dialog: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loading = true;
          // Process permanent address when dialog opens
          this.processPermanentAddress();
          setTimeout(() => {
            this.loading = false;
            if (this.studentData?.usn_id) this.loadProfileImage();
          }, 300);
        } else {
          this.cleanup();
        }
      },
    },
    studentData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal?.usn_id && this.dialog) {
          this.processPermanentAddress();
          this.loadProfileImage();
          this.getPayments();
          this.getAttachments();
          this.getProofs();
        }
      },
    },
  },

  methods: {
    openScorecard() {
      this.scorecardDialog = true;
    },
    closeScorecard() {
      this.scorecardDialog = false;
    },
    gotoTop() {
      window.scrollTo({ top: 0, behavior: "auto" });
    },

    processPermanentAddress() {
      // Check if permanent address should be same as current address
      if (this.isPermanentAddressSame) {
        // If permanent_address is empty or doesn't exist, copy from current_address
        if (
          !this.studentData.student_info.permanent_address ||
          Object.keys(this.studentData.student_info.permanent_address)
            .length === 0
        ) {
          // Use Vue.set to make it reactive
          this.$set(this.studentData.student_info, "permanent_address", {
            ...this.studentData.student_info.current_address,
          });
        }
      }
    }, 
 
    getStatusColor(status) {
      const colors = {
        Active: "success",
        Inactive: "warning",
        Discontinued: "error",
        Graduated: "primary",
        Pending: "info",
        Completed: "success",
        Ongoing: "primary",
      };
      return colors[status] || "grey";
    },

    getFieldValue(path) {
      const keys = path.split(".");
      let value = this.studentData;

      for (const key of keys) {
        if (value === null || value === undefined) return "—";
        if (key === "currentaddress" || key === "permanentaddress") return "";
        value = value[key];
      }

      // Special handling for permanent address fields when is_permanent_address_same is true
      if (path.includes("permanent_address") && this.isPermanentAddressSame) {
        // If the permanent address field is empty, try to get from current address
        if (!value || value === "—") {
          const currentPath = path.replace(
            "permanent_address",
            "current_address",
          );
          const currentKeys = currentPath.split(".");
          let currentValue = this.studentData;
          for (const key of currentKeys) {
            if (currentValue === null || currentValue === undefined) break;
            currentValue = currentValue[key];
          }
          if (currentValue && currentValue !== "—") {
            value = currentValue;
          }
        }
      }

      if (path === "student_info.dob" && value) {
        return new Date(value).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      }
      if (path === "student_info.mobile_no" && value) {
        const prefix = this.studentData.student_info?.mobile_prefix || "";
        return prefix ? `${prefix} ${value}` : value;
      }
      if (path === "student_info.whatsapp_mobile_no" && value) {
        const prefix =
          this.studentData.student_info?.whatsapp_mobile_prefix || "";
        return prefix ? `${prefix} ${value}` : value;
      }
      return value || "—";
    },

    hasData(path) {
      const value = this.getFieldValue(path);
      return (
        value !== "—" && value !== null && value !== undefined && value !== ""
      );
    },

    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    getPartnerName(id) {
      const partner = this.partners.find((p) => p.id === id);
      return partner ? partner.name : "";
    },

    async loadProfileImage() {
      if (!this.studentData?.usn_id) return;
      this.imageLoading = true;
      this.imageError = false;
      if (this.profilePreview) {
        window.URL.revokeObjectURL(this.profilePreview);
        this.profilePreview = null;
      }
      try {
        const response = await this.$axios.get(
          `/get_profile_image/${this.studentData.usn_id}`,
          { responseType: "blob" },
        );
        if (response.data && response.data.size > 0) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          setTimeout(() => {
            this.profilePreview = url;
            this.imageLoading = false;
          }, 300);
        } else {
          this.imageError = true;
          this.imageLoading = false;
        }
      } catch (error) {
        this.imageError = true;
        this.imageLoading = false;
      }
    },

    handleImageError() {
      this.imageError = true;
      this.profilePreview = null;
    },

    async getPayments() {
      try {
        const response = await this.$axios.get(
          `/get_student_payments/${this.studentData.application_id}/${this.studentData.usn_id}`,
        );
        this.payments = (response.data.payments || []).sort((a, b) => {
          const [d1, m1, y1] = a.date.split("-");
          const [d2, m2, y2] = b.date.split("-");
          return new Date(`${y2}-${m2}-${d2}`) - new Date(`${y1}-${m1}-${d1}`);
        });
      } catch (error) {
        console.error("Error fetching payments:", error);
        this.payments = [];
      }
    },

    formatAmount(amount) {
      return amount.replace("₹", "").trim();
    },

    async downloadReceipt(payment) {
      payment.amount = payment.amount.replace("₹", "");
      try {
        const response = await this.$axios.get(
          `/download_student_receipt/${payment.reference_number}/${this.studentData.usn_id}`,
          { responseType: "blob" },
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${this.studentData.usn_id}_${payment.particular}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading receipt:", error);
      }
    },

    viewPaymentDetails(payment) {
      this.selectedPayment = payment;
      this.paymentDetailsDialog = true;
    },

    async getAttachments() {
      this.loadingAttachments = true;
      try {
        const response = await this.$axios.get(
          `/get_applicant_proofs/${this.studentData.usn_id}`,
        );
        if (response.data.success) {
          this.attachments = (response.data.data || []).map((file) => ({
            ...file,
            blobUrl: this.createBlobUrl(file.content, file.type),
          }));
        }
      } catch (error) {
        console.error("Failed to load attachments:", error);
      } finally {
        this.loadingAttachments = false;
      }
    },

    async getProofs() {
      this.loadingProofs = true;
      try {
        const response = await this.$axios.get(
          `/get_proofs/${this.studentData.usn_id}`,
        );
        if (response.data.success) {
          this.proofs = (response.data.data || []).map((file) => ({
            ...file,
            blobUrl: this.createBlobUrl(file.content, file.type),
          }));
        }
      } catch (error) {
        console.error("Failed to load proofs:", error);
      } finally {
        this.loadingProofs = false;
      }
    },

    createBlobUrl(base64Content, mimeType) {
      try {
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++)
          bytes[i] = binaryString.charCodeAt(i);
        const blob = new Blob([bytes], { type: mimeType });
        return window.URL.createObjectURL(blob);
      } catch (error) {
        return null;
      }
    },

    getDocType(filename) {
      const parts = filename.split("_");
      if (parts.length > 1) {
        const docType = parts[1].split(".")[0];
        return docType.charAt(0).toUpperCase() + docType.slice(1);
      }
      return "Document";
    },

    getDocTypeColor(filename) {
      const docType = this.getDocType(filename).toLowerCase();
      const colorMap = { aadhar: "blue", pan: "purple" };
      return colorMap[docType] || "grey";
    },

    getProofLabel(filename) {
      const base = filename.replace(/\.[^.]+$/, "");
      const labels = {
        name: "Name Proof",
        dob: "DOB Proof",
        address: "Address Proof",
        employee_id: "SAP/Employee ID Proof",
      };
      const match = base.match(/(employee_id|name|dob|address)_proof/i);
      if (match) return labels[match[1].toLowerCase()];
      return base
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
    },

    getProofColor(filename) {
      const colorMap = {
        "Name Proof": "indigo",
        "DOB Proof": "teal",
        "Address Proof": "orange darken-2",
        "SAP/Employee ID Proof": "deep-purple",
      };
      return colorMap[this.getProofLabel(filename)] || "blue-grey";
    },

    isImage(mimeType) {
      return mimeType && mimeType.startsWith("image/");
    },
    isPDF(mimeType) {
      return mimeType === "application/pdf";
    },

    downloadAttachment(attachment) {
      try {
        const link = document.createElement("a");
        link.href = attachment.blobUrl;
        link.download = attachment.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.$store.commit("showSnackbar", {
          message: "File downloaded successfully",
          color: "success",
        });
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: "Failed to download file",
          color: "error",
        });
      }
    },

    viewAttachment(attachment) {
      const idx = this.sortedAttachments.findIndex(
        (a) => a.name === attachment.name,
      );
      this.currentAttachmentIndex = idx >= 0 ? idx : 0;
      this.selectedAttachment =
        this.sortedAttachments[this.currentAttachmentIndex];
      this._resetTransform();
      this.viewDialog = true;
    },

    closeViewer() {
      this.viewDialog = false;
      this.selectedAttachment = null;
      this.currentAttachmentIndex = 0;
      this._resetTransform();
    },

    showPreviousAttachment() {
      if (this.currentAttachmentIndex > 0) {
        this.currentAttachmentIndex--;
        this._syncAttachment();
      }
    },

    showNextAttachment() {
      if (this.currentAttachmentIndex < this.sortedAttachments.length - 1) {
        this.currentAttachmentIndex++;
        this._syncAttachment();
      }
    },

    goToAttachment(index) {
      this.currentAttachmentIndex = index;
      this._syncAttachment();
      this._scrollThumbIntoView(index);
    },

    _syncAttachment() {
      this.selectedAttachment =
        this.sortedAttachments[this.currentAttachmentIndex];
      this._resetTransform();
      this._scrollThumbIntoView(this.currentAttachmentIndex);
    },

    _resetTransform() {
      this.zoomLevel = 1;
      this.panX = 0;
      this.panY = 0;
      this.rotateDeg = 0;
      this.isPanning = false;
      this.isDragging = false;
    },

    onImageLoad() {
      this._resetTransform();
    },

    resetView() {
      this._resetTransform();
    },

    zoomIn() {
      this._applyZoom(this.zoomLevel * 1.25);
    },

    zoomOut() {
      this._applyZoom(this.zoomLevel / 1.25);
    },

    _applyZoom(newZoom, focalClientX, focalClientY) {
      const clamped = Math.min(Math.max(newZoom, this.minZoom), this.maxZoom);
      const stage = this.$refs.imageStage;

      if (stage && focalClientX !== undefined && focalClientY !== undefined) {
        const rect = stage.getBoundingClientRect();

        const fx = focalClientX - rect.left - rect.width / 2;
        const fy = focalClientY - rect.top - rect.height / 2;
        const ratio = clamped / this.zoomLevel;
        this.panX = fx - ratio * (fx - this.panX);
        this.panY = fy - ratio * (fy - this.panY);
      }

      this.zoomLevel = clamped;

      if (clamped <= 1) {
        this.panX = 0;
        this.panY = 0;
      }
    },

    handleWheel(e) {
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      this._applyZoom(this.zoomLevel * factor, e.clientX, e.clientY);
    },

    startDrag(e) {
      if (!this.drag) return;
      if (this.zoomLevel <= 1) return;
      this.drag.active = true;
      this.isDragging = true;
      this.isPanning = true;
      this.drag.startX = e.clientX;
      this.drag.startY = e.clientY;
      this.drag.originPanX = this.panX;
      this.drag.originPanY = this.panY;
    },

    onDrag(e) {
      if (!this.drag || !this.drag.active) return;
      this.panX = this.drag.originPanX + (e.clientX - this.drag.startX);
      this.panY = this.drag.originPanY + (e.clientY - this.drag.startY);
    },

    endDrag() {
      if (!this.drag) return;
      this.drag.active = false;
      this.isDragging = false;
      this.isPanning = false;
    },

    handleTouchStart(e) {
      if (e.touches.length === 1) {
        this._touch.startX = e.touches[0].clientX;
        this._touch.startY = e.touches[0].clientY;
        this._touch.lastDist = null;

        if (this.zoomLevel > 1) {
          this.drag.active = true;
          this.isDragging = true;
          this.drag.startX = e.touches[0].clientX;
          this.drag.startY = e.touches[0].clientY;
          this.drag.originPanX = this.panX;
          this.drag.originPanY = this.panY;
        }
      } else if (e.touches.length === 2) {
        this.drag.active = false;
        this.isDragging = false;
        this._touch.lastDist = this._getTouchDist(e.touches);
      }
    },

    handleTouchMove(e) {
      if (e.touches.length === 2) {
        const dist = this._getTouchDist(e.touches);
        if (this._touch.lastDist) {
          const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          this._applyZoom(
            this.zoomLevel * (dist / this._touch.lastDist),
            midX,
            midY,
          );
        }
        this._touch.lastDist = dist;
      } else if (
        e.touches.length === 1 &&
        this.zoomLevel > 1 &&
        this.drag.active
      ) {
        this.panX =
          this.drag.originPanX + (e.touches[0].clientX - this.drag.startX);
        this.panY =
          this.drag.originPanY + (e.touches[0].clientY - this.drag.startY);
      }
    },

    handleTouchEnd(e) {
      if (this.zoomLevel <= 1 && e.changedTouches.length === 1) {
        const dx = e.changedTouches[0].clientX - (this._touch.startX || 0);
        const dy = e.changedTouches[0].clientY - (this._touch.startY || 0);
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.showNextAttachment();
          else this.showPreviousAttachment();
        }
      }
      this.drag.active = false;
      this.isDragging = false;
      this.isPanning = false;
      this._touch.lastDist = null;
    },

    _getTouchDist(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },

    rotateLeft() {
      this.rotateDeg -= 90;
    },

    rotateRight() {
      this.rotateDeg += 90;
    },

    resetRotate() {
      this.rotateDeg = 0;
    },

    handleKeyNavigation(e) {
      if (!this.viewDialog) return;
      switch (e.key) {
        case "ArrowLeft":
          this.showPreviousAttachment();
          break;
        case "ArrowRight":
          this.showNextAttachment();
          break;
        case "Escape":
          this.closeViewer();
          break;
        case "+":
        case "=":
          this.zoomIn();
          break;
        case "-":
          this.zoomOut();
          break;
        case "0":
          this.resetView();
          break;
        case "r":
          this.rotateRight();
          break;
        case "l":
          this.rotateLeft();
          break;
      }
    },

    _scrollThumbIntoView(index) {
      this.$nextTick(() => {
        const refs = this.$refs[`thumb_${index}`];
        const el = Array.isArray(refs) ? refs[0] : refs;
        if (el)
          el.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
      });
    },

    getShortFileName(name) {
      return name.length > 14 ? name.substring(0, 12) + "…" : name;
    },

    cleanup() {
      if (this.profilePreview) window.URL.revokeObjectURL(this.profilePreview);
      this.profilePreview = null;
      this.imageLoading = false;
      this.imageError = false;
      this.showAllPayments = false;
      this.paymentDetailsDialog = false;
      this.selectedPayment = null;
      this.payments = [];
      this.attachments.forEach((a) => {
        if (a.blobUrl) window.URL.revokeObjectURL(a.blobUrl);
      });
      this.attachments = [];
      this.proofs.forEach((p) => {
        if (p.blobUrl) window.URL.revokeObjectURL(p.blobUrl);
      });
      this.proofs = [];
      this._resetTransform();
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.student-profile-card {
  border-left: 4px solid var(--v-primary-base) !important;
}
.profile-image-container {
  position: relative;
}
.status-chip {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.info-item {
  padding: 8px 0;
}
.info-label {
  font-size: 0.75rem;
  color: #78909c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 2px;
}
.info-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #37474f;
}

.section-card {
  border-radius: 8px;
  overflow: hidden;
}
.section-title {
  background-color: #f8f9fa;
  padding: 12px 16px !important;
}
.field-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.field-item:last-child {
  border-bottom: none;
}
.field-item.has-data .field-value {
  color: #263238;
}
.field-item:not(.has-data) .field-value {
  color: #78909c;
}
.field-label {
  font-size: 0.75rem;
  color: #546e7a;
  font-weight: 500;
  margin-bottom: 2px;
}
.field-value {
  font-size: 0.875rem;
  font-weight: 500;
}

.payment-table-wrapper {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 4px;
}
.payment-table thead th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
  font-weight: 600;
  color: #37474f;
  font-size: 0.875rem;
  padding: 12px 8px !important;
}
.payment-table tbody td {
  padding: 12px 8px !important;
  vertical-align: middle;
}
.payment-row {
  transition: background-color 0.2s ease;
}
.payment-row:hover {
  background-color: #f5f7fa !important;
}
.payment-date {
  font-size: 0.875rem;
  font-weight: 500;
  color: #37474f;
}
.reference-chip {
  font-family: "Roboto Mono", monospace;
  font-size: 0.75rem;
}
.payment-particular {
  font-size: 0.875rem;
  font-weight: 500;
  color: #37474f;
}
.payment-amount {
  font-size: 0.95rem;
}
.action-btn {
  transition: all 0.2s ease;
}
.action-btn:hover {
  transform: scale(1.1);
}
.view-more-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.5px;
}
.payment-table-wrapper::-webkit-scrollbar {
  width: 6px;
}
.payment-table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.payment-table-wrapper::-webkit-scrollbar-thumb {
  background: #b0bec5;
  border-radius: 4px;
}
.payment-table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #78909c;
}

.attachment-card {
  height: 100%;
  transition: all 0.3s ease;
}
.attachment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.attachment-preview {
  min-height: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pdf-preview,
.other-file-preview {
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border: 2px dashed #ddd;
  width: 100%;
}
.cursor-pointer {
  cursor: pointer;
}
.hover-card {
  transition: all 0.3s ease;
}

.av-root {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  height: 700px;
  max-height: 92vh;
}

.av-header {
  flex: 0 0 52px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 8px;
}
.av-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.av-divider {
  flex: 0 0 1px;
  background: rgba(0, 0, 0, 0.12);
  width: 100%;
}

.av-image-stage {
  flex: 1 1 0;
  position: relative;
  background: #1c1c1e;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  -webkit-user-select: none;
  min-height: 0;
}
.av-image-canvas {
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
  transition: transform 0.18s ease;
}
.av-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  pointer-events: none;
  border-radius: 3px;
  transition: transform 0.2s ease;
}

.av-zoom-bar {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 999px;
  padding: 4px 10px;
  z-index: 20;
  pointer-events: all;
}
.av-zoom-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  transition: background 0.15s;
}
.av-zoom-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}
.av-zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.av-zoom-pct {
  color: #fff;
  font-size: 12px;
  min-width: 42px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.av-zoom-sep {
  display: inline-block;
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.25);
  margin: 0 4px;
  align-self: center;
}

.av-pdf-stage {
  flex: 1 1 0;
  min-height: 0;
  background: #525659;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.av-pdf-stage iframe {
  flex: 1 1 0;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: none;
  display: block;
}

.av-unsupported {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #1c1c1e;
  padding: 32px;
}
.av-unsupported-title {
  color: #aaa;
  font-size: 16px;
  margin: 0;
}
.av-unsupported-sub {
  color: #777;
  font-size: 13px;
  margin: 0;
}

.av-thumb-strip {
  flex: 0 0 auto;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 10px 14px;
  background: #f5f5f5;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: #ccc #f5f5f5;
}
.av-thumb-strip::-webkit-scrollbar {
  height: 4px;
}
.av-thumb-strip::-webkit-scrollbar-track {
  background: #eee;
}
.av-thumb-strip::-webkit-scrollbar-thumb {
  background: #bbb;
  border-radius: 4px;
}

.av-thumb-item {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: transform 0.15s ease;
}
.av-thumb-item:hover {
  transform: translateY(-2px);
}

.av-thumb-box {
  width: 62px;
  height: 62px;
  border-radius: 8px;
  overflow: hidden;
  border: 2.5px solid transparent;
  transition: border-color 0.15s, box-shadow 0.15s;
  background: #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
}
.av-thumb-active .av-thumb-box {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.22);
}
.av-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.av-thumb-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f0f0f0;
}
.av-thumb-label {
  font-size: 10px;
  color: #555;
  max-width: 66px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.animate__delay-1s {
  animation-delay: 0.1s;
}
.animate__delay-2s {
  animation-delay: 0.2s;
}
.animate__delay-3s {
  animation-delay: 0.3s;
}
.animate__delay-4s {
  animation-delay: 0.4s;
}

@media (max-width: 600px) {
  .av-root {
    height: 600px;
  }
  .av-thumb-box {
    width: 48px;
    height: 48px;
  }
  .av-thumb-label {
    max-width: 52px;
  }
  .av-zoom-bar {
    bottom: 10px;
    padding: 4px 8px;
  }
}

@media print {
  .v-dialog {
    position: relative !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .hover-card {
    box-shadow: none !important;
  }
  .payment-table-wrapper {
    max-height: none !important;
    overflow: visible !important;
  }
  .action-btn,
  .view-more-btn {
    display: none !important;
  }
}
</style>
